import {
    dayOfWeekOptions,
    formatDayOfWeekLabel,
    getDayOfWeekOptions,
    normalizeDayOfWeekList,
    priceTypeOptions,
} from "../tariffOptions";

export const ENERGY_MODE = {
    FIXED: "fixed",
    TIME: "time",
    POWER: "power",
    SPOT: "spot",
};

export const WIZARD_STEPS = [
    "basics",
    "energyPrice",
    "extraFees",
    "assignment",
    "preview",
];

const emptyFee = () => ({ enabled: false, price: "", vat: "", stepSize: 60 });

const emptyLaneFee = () => ({
    ...emptyFee(),
    timePrice: "",
    timeVat: "",
    timeStepSize: 60,
});

const BOOKING_TYPES = ["BOOKING", "BOOKING_EXPIRES", "BOOKING_CANCELLATION_FEES", "BOOKING_OVERTIME"];
const RESERVATION_TYPES = ["RESERVATION", "RESERVATION_EXPIRES"];
const PRICE_COMPONENT_TYPES = ["ENERGY", "TIME", "PARKING_TIME", "FLAT"];

function elementPublicId(element) {
    return element?.tariffElementPublicId || element?.TariffElementPublicId || null;
}

function getRestrictions(element) {
    return element?.restrictions || element?.Restrictions || {};
}

function restrictionField(restrictions, name) {
    if (!restrictions) {
        return "";
    }
    const pascal = name.charAt(0).toUpperCase() + name.slice(1);
    return restrictions[name] ?? restrictions[pascal] ?? "";
}

function normalizeEnum(value, names) {
    if (value == null || value === "") {
        return "";
    }
    if (typeof value === "number" && names[value]) {
        return names[value];
    }
    return String(value).toUpperCase();
}

function getBooking(element) {
    return normalizeEnum(restrictionField(getRestrictions(element), "booking"), BOOKING_TYPES);
}

function getReservation(element) {
    return normalizeEnum(restrictionField(getRestrictions(element), "reservation"), RESERVATION_TYPES);
}

function componentType(pc) {
    const value = pc?.type ?? pc?.Type;
    return normalizeEnum(value, PRICE_COMPONENT_TYPES);
}

export function createEmptyWizardState() {
    return {
        basics: {
            tariffName: "",
            currency: "SEK",
            countryCode: "",
            tariffType: "REGULAR",
            taxIncluded: "NO",
            isPublic: false,
            startDateTimeUtc: "",
            endDateTimeUtc: "",
            minPriceExclVat: null,
            minPriceInclVat: null,
            maxPriceExclVat: null,
            maxPriceInclVat: null,
            preauthorizeAmount: null,
            altTexts: [],
        },
        energyMode: ENERGY_MODE.FIXED,
        energy: {
            price: "",
            vat: "",
            energyPriceMinExclVat: "",
            energyPriceMaxExclVat: "",
            periods: [],
            bands: [createEmptyBand()],
        },
        extras: {
            chargingTime: emptyFee(),
            parkingTime: emptyFee(),
            startFee: emptyFee(),
            reservation: emptyLaneFee(),
            booking: emptyLaneFee(),
            noShow: emptyFee(),
            overtime: emptyLaneFee(),
            cancellation: emptyLaneFee(),
        },
        assignment: {
            chargingConnectorPublicIds: [],
            customerPublicIds: [],
        },
        replaceOverlapping: false,
        legacyElements: [],
    };
}

export function createEmptyPeriod() {
    return {
        clientId: newClientId(),
        days: [],
        startTime: "",
        endTime: "",
        price: "",
        vat: "",
        tariffElementPublicId: null,
    };
}

export function createEmptyBand() {
    return {
        clientId: newClientId(),
        minPower: "",
        maxPower: "",
        price: "",
        vat: "",
        tariffElementPublicId: null,
    };
}

function newClientId() {
    return `el_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function toDateTimeLocal(value) {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value).slice(0, 16);
    }
    const pad = (part) => String(part).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toApiDateTime(value) {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function toTimeInput(value) {
    if (!value) {
        return "";
    }
    const text = String(value);
    if (text.length >= 5) {
        return text.slice(0, 5);
    }
    return text;
}

function minutesOfDay(value) {
    const match = String(value || "").match(/^(\d{2}):(\d{2})/);
    if (!match) {
        return null;
    }
    return Number(match[1]) * 60 + Number(match[2]);
}

function periodCrossesMidnight(startTime, endTime) {
    const start = minutesOfDay(startTime);
    const end = minutesOfDay(endTime);
    if (start == null || end == null) {
        return false;
    }
    if (end === 0) {
        return false;
    }
    return end <= start;
}

function toApiTime(value) {
    if (!value) {
        return undefined;
    }
    if (/^\d{2}:\d{2}$/.test(value)) {
        return `${value}:00`;
    }
    return value;
}

function toNumberOrEmpty(value) {
    if (value === "" || value == null) {
        return "";
    }
    return String(value);
}

function parsePrice(value) {
    if (value === "" || value == null) {
        return 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function parseVat(value) {
    if (value === "" || value == null) {
        return 0;
    }
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
}

function parseStepSize(value) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function typeLabel(type) {
    return priceTypeOptions.find((option) => option.value === type)?.label || type;
}

function priceComponent(type, price, vat, stepSize) {
    return {
        type,
        typeDisplayName: typeLabel(type),
        price: parsePrice(price),
        vat: parseVat(vat),
        stepSize: type === "ENERGY" || type === "FLAT" ? 1 : parseStepSize(stepSize),
    };
}

function chargingExtrasComponents(extras) {
    const components = [];
    if (extras.chargingTime.enabled) {
        components.push(
            priceComponent(
                "TIME",
                extras.chargingTime.price,
                extras.chargingTime.vat,
                extras.chargingTime.stepSize
            )
        );
    }
    if (extras.parkingTime.enabled) {
        components.push(
            priceComponent(
                "PARKING_TIME",
                extras.parkingTime.price,
                extras.parkingTime.vat,
                extras.parkingTime.stepSize
            )
        );
    }
    if (extras.startFee.enabled) {
        components.push(priceComponent("FLAT", extras.startFee.price, extras.startFee.vat, 1));
    }
    return components;
}

function hasChargingExtras(extras) {
    return extras.chargingTime.enabled || extras.parkingTime.enabled || extras.startFee.enabled;
}

function hasOptionalTimePrice(fee) {
    return fee.timePrice !== "" && fee.timePrice != null;
}

function laneElement(fee, restrictions) {
    const components = [priceComponent("FLAT", fee.price, fee.vat, 1)];
    if (hasOptionalTimePrice(fee)) {
        components.push(priceComponent("TIME", fee.timePrice, fee.timeVat, fee.timeStepSize));
    }
    return {
        tariffElementPublicId: fee.tariffElementPublicId || undefined,
        restrictions,
        priceComponents: components,
    };
}

function laneFeeFromElement(element) {
    const flat = findComponent(element, "FLAT");
    const time = findComponent(element, "TIME");
    return {
        enabled: true,
        price: toNumberOrEmpty(componentPrice(flat)),
        vat: toNumberOrEmpty(componentVat(flat)),
        stepSize: 1,
        timePrice: toNumberOrEmpty(componentPrice(time)),
        timeVat: toNumberOrEmpty(componentVat(time)),
        timeStepSize: componentStepSize(time) || 60,
        tariffElementPublicId: elementPublicId(element),
    };
}

function componentPrice(pc) {
    return pc?.price ?? pc?.Price;
}

function componentVat(pc) {
    return pc?.vat ?? pc?.Vat;
}

function componentStepSize(pc) {
    return pc?.stepSize ?? pc?.StepSize;
}

function isReservationElement(element) {
    return Boolean(getReservation(element));
}

function isBookingElement(element) {
    return Boolean(getBooking(element));
}

function hasFallbackEnergy(energy) {
    return energy?.price !== "" && energy?.price != null;
}

function appendUnrestrictedFallback(generated, state, extras, chargingExtras) {
    const fallbackComponents = [];
    if (hasFallbackEnergy(state.energy)) {
        fallbackComponents.push(priceComponent("ENERGY", state.energy.price, state.energy.vat, 1));
    }
    if (hasChargingExtras(extras)) {
        fallbackComponents.push(...chargingExtras);
    }
    if (fallbackComponents.length) {
        generated.push({
            tariffElementPublicId:
                state.energy.fixedElementPublicId || state.energy.extrasElementPublicId || undefined,
            restrictions: null,
            priceComponents: fallbackComponents,
        });
    }
}

function isChargingElement(element) {
    return !isReservationElement(element) && !isBookingElement(element);
}

function hasTimeWindow(restrictions) {
    return Boolean(
        restrictions?.startTime ||
        restrictions?.StartTime ||
        restrictions?.endTime ||
        restrictions?.EndTime ||
        (restrictions?.dayOfWeek || restrictions?.DayOfWeek || []).length
    );
}

function hasPowerWindow(restrictions) {
    return (
        restrictions?.minPower != null ||
        restrictions?.MinPower != null ||
        restrictions?.maxPower != null ||
        restrictions?.MaxPower != null
    );
}

function appendChargingPriceElements(generated, state, extras, chargingExtras) {
    const energyId = state.energy.fixedElementPublicId || undefined;
    const extrasId = state.energy.extrasElementPublicId || undefined;
    const splitExtras = Boolean(extrasId && extrasId !== energyId && hasChargingExtras(extras));

    if (splitExtras) {
        if (
            state.energyMode === ENERGY_MODE.FIXED ||
            state.energyMode === ENERGY_MODE.SPOT ||
            hasFallbackEnergy(state.energy)
        ) {
            generated.push({
                tariffElementPublicId: energyId,
                restrictions: null,
                priceComponents: [priceComponent("ENERGY", state.energy.price, state.energy.vat, 1)],
            });
        }
        generated.push({
            tariffElementPublicId: extrasId,
            restrictions: null,
            priceComponents: chargingExtras,
        });
        return;
    }

    if (state.energyMode === ENERGY_MODE.FIXED || state.energyMode === ENERGY_MODE.SPOT) {
        generated.push({
            tariffElementPublicId: energyId,
            restrictions: null,
            priceComponents: [
                priceComponent("ENERGY", state.energy.price, state.energy.vat, 1),
                ...chargingExtras,
            ],
        });
        return;
    }

    appendUnrestrictedFallback(generated, state, extras, chargingExtras);
}

function getComponents(element) {
    return element?.priceComponents || element?.price_components || element?.PriceComponents || [];
}

function findComponent(element, type) {
    return getComponents(element).find((pc) => componentType(pc) === type);
}

export function toElements(state) {
    const extras = state.extras;
    const chargingExtras = chargingExtrasComponents(extras);
    const generated = [];

    if (state.energyMode === ENERGY_MODE.TIME) {
        (state.energy.periods || []).forEach((period) => {
            const restrictions = {};
            const days = normalizeDayOfWeekList(period.days);
            if (days.length) {
                restrictions.dayOfWeek = days;
            }
            if (period.startTime) {
                restrictions.startTime = toApiTime(period.startTime);
            }
            if (period.endTime) {
                restrictions.endTime = toApiTime(period.endTime);
            }
            generated.push({
                tariffElementPublicId: period.tariffElementPublicId || undefined,
                restrictions: Object.keys(restrictions).length ? restrictions : null,
                priceComponents: [priceComponent("ENERGY", period.price, period.vat, 1)],
            });
        });
        appendChargingPriceElements(generated, state, extras, chargingExtras);
    } else if (state.energyMode === ENERGY_MODE.POWER) {
        (state.energy.bands || []).forEach((band) => {
            const restrictions = {};
            if (band.minPower !== "" && band.minPower != null) {
                restrictions.minPower = Number(band.minPower);
            }
            if (band.maxPower !== "" && band.maxPower != null) {
                restrictions.maxPower = Number(band.maxPower);
            }
            generated.push({
                tariffElementPublicId: band.tariffElementPublicId || undefined,
                restrictions: Object.keys(restrictions).length ? restrictions : null,
                priceComponents: [priceComponent("ENERGY", band.price, band.vat, 1)],
            });
        });
        appendChargingPriceElements(generated, state, extras, chargingExtras);
    } else {
        appendChargingPriceElements(generated, state, extras, chargingExtras);
    }

    if (extras.reservation.enabled) {
        const restrictionType = extras.reservation.restrictionType || "RESERVATION";
        generated.push(
            laneElement(
                extras.reservation,
                restrictionType === "BOOKING"
                    ? { booking: "BOOKING" }
                    : { reservation: "RESERVATION" }
            )
        );
    }

    if (extras.booking?.enabled) {
        generated.push(laneElement(extras.booking, { booking: "BOOKING" }));
    }

    if (extras.noShow.enabled) {
        const noShowType = extras.noShow.restrictionType || "BOOKING_EXPIRES";
        generated.push({
            tariffElementPublicId: extras.noShow.tariffElementPublicId || undefined,
            restrictions:
                noShowType === "RESERVATION_EXPIRES"
                    ? { reservation: "RESERVATION_EXPIRES" }
                    : { booking: noShowType },
            priceComponents: [priceComponent("FLAT", extras.noShow.price, extras.noShow.vat, 1)],
        });
        (extras.noShow.additionalElements || []).forEach((element) => {
            generated.push(element);
        });
    }

    if (extras.overtime?.enabled) {
        generated.push(laneElement(extras.overtime, { booking: "BOOKING_OVERTIME" }));
    }

    if (extras.cancellation?.enabled) {
        generated.push(
            laneElement(extras.cancellation, { booking: "BOOKING_CANCELLATION_FEES" })
        );
    }

    (extras.additionalBookingElements || []).forEach((element) => {
        generated.push(element);
    });

    const mappedLane = (element) => {
        const booking = getBooking(element);
        const reservation = getReservation(element);
        return (
            reservation === "RESERVATION" ||
            reservation === "RESERVATION_EXPIRES" ||
            booking === "BOOKING" ||
            booking === "BOOKING_EXPIRES" ||
            booking === "BOOKING_OVERTIME" ||
            booking === "BOOKING_CANCELLATION_FEES"
        );
    };
    const legacy = (state.legacyElements || []).filter((element) => !mappedLane(element));
    return [...generated, ...legacy];
}

export function fromCompleteTariff(tariff) {
    const state = createEmptyWizardState();
    if (!tariff) {
        return state;
    }

    state.basics = {
        tariffName: tariff.tariffName || "",
        currency: tariff.currency || "SEK",
        countryCode: tariff.countryCode || "",
        tariffType: tariff.tariffType || "REGULAR",
        taxIncluded: "NO",
        isPublic: Boolean(tariff.isPublic),
        startDateTimeUtc: toDateTimeLocal(tariff.startDateTimeUtc),
        endDateTimeUtc: toDateTimeLocal(tariff.endDateTimeUtc),
        minPriceExclVat: tariff.minPrice?.excl_vat ?? tariff.minPriceExclVat ?? null,
        minPriceInclVat: tariff.minPrice?.incl_vat ?? tariff.minPriceInclVat ?? null,
        maxPriceExclVat: tariff.maxPrice?.excl_vat ?? tariff.maxPriceExclVat ?? null,
        maxPriceInclVat: tariff.maxPrice?.incl_vat ?? tariff.maxPriceInclVat ?? null,
        preauthorizeAmount: tariff.preauthorizeAmount ?? null,
        altTexts: (tariff.tariffAltTexts || tariff.altTexts || []).map((alt) => ({
            text: alt.text || "",
            language: alt.language || "",
        })),
    };

    state.assignment = {
        chargingConnectorPublicIds: (tariff.chargingConnectors || []).map(
            (connector) => connector.chargingConnectorPublicId
        ),
        customerPublicIds: (tariff.customers || []).map((customer) => customer.customerPublicId),
    };

    const elements = (tariff.tariffElements || []).filter((element) => !element.isDeleted);
    const charging = elements.filter(isChargingElement);
    const reservationEls = elements.filter((element) => getReservation(element) === "RESERVATION");
    const bookingFeeEls = elements.filter((element) => getBooking(element) === "BOOKING");
    const noShowEls = elements.filter((element) => {
        const booking = getBooking(element);
        const reservation = getReservation(element);
        return booking === "BOOKING_EXPIRES" || reservation === "RESERVATION_EXPIRES";
    });
    const overtimeEls = elements.filter((element) => getBooking(element) === "BOOKING_OVERTIME");
    const cancellationEls = elements.filter(
        (element) => getBooking(element) === "BOOKING_CANCELLATION_FEES"
    );
    const used = new Set();

    const energyElements = charging.filter((element) => findComponent(element, "ENERGY"));
    const timeEnergy = energyElements.filter((element) => hasTimeWindow(getRestrictions(element)));
    const powerEnergy = energyElements.filter(
        (element) => hasPowerWindow(getRestrictions(element)) && !hasTimeWindow(getRestrictions(element))
    );
    const unrestrictedEnergy = energyElements.filter((element) => {
        const restrictions = getRestrictions(element);
        return !hasTimeWindow(restrictions) && !hasPowerWindow(restrictions);
    });

    if (timeEnergy.length && powerEnergy.length === 0) {
        state.energyMode = ENERGY_MODE.TIME;
        state.energy.periods = timeEnergy.map((element) => {
            used.add(element);
            const energy = findComponent(element, "ENERGY");
            const restrictions = getRestrictions(element);
            return {
                clientId: newClientId(),
                days: normalizeDayOfWeekList(restrictions.dayOfWeek || restrictions.DayOfWeek || []),
                startTime: toTimeInput(restrictions.startTime || restrictions.StartTime),
                endTime: toTimeInput(restrictions.endTime || restrictions.EndTime),
                price: toNumberOrEmpty(componentPrice(energy)),
                vat: toNumberOrEmpty(componentVat(energy)),
                tariffElementPublicId: elementPublicId(element),
            };
        });
        if (unrestrictedEnergy[0]) {
            const element = unrestrictedEnergy[0];
            used.add(element);
            const energy = findComponent(element, "ENERGY");
            state.energy.price = toNumberOrEmpty(energy?.price);
            state.energy.vat = toNumberOrEmpty(energy?.vat);
            state.energy.fixedElementPublicId = elementPublicId(element);
            applyChargingExtrasFromElement(state, element);
        }
    } else if (powerEnergy.length && timeEnergy.length === 0) {
        state.energyMode = ENERGY_MODE.POWER;
        state.energy.bands = powerEnergy.map((element) => {
            used.add(element);
            const energy = findComponent(element, "ENERGY");
            const restrictions = getRestrictions(element);
            return {
                clientId: newClientId(),
                minPower: toNumberOrEmpty(restrictions.minPower ?? restrictions.MinPower),
                maxPower: toNumberOrEmpty(restrictions.maxPower ?? restrictions.MaxPower),
                price: toNumberOrEmpty(energy?.price),
                vat: toNumberOrEmpty(energy?.vat),
                tariffElementPublicId: elementPublicId(element),
            };
        });
        if (!state.energy.bands.length) {
            state.energy.bands = [createEmptyBand()];
        }
        if (unrestrictedEnergy[0]) {
            const element = unrestrictedEnergy[0];
            used.add(element);
            const energy = findComponent(element, "ENERGY");
            state.energy.price = toNumberOrEmpty(energy?.price);
            state.energy.vat = toNumberOrEmpty(energy?.vat);
            state.energy.fixedElementPublicId = elementPublicId(element);
            applyChargingExtrasFromElement(state, element);
        }
    } else if (unrestrictedEnergy.length) {
        state.energyMode = ENERGY_MODE.FIXED;
        const element = unrestrictedEnergy[0];
        used.add(element);
        const energy = findComponent(element, "ENERGY");
        state.energy.price = toNumberOrEmpty(energy?.price);
        state.energy.vat = toNumberOrEmpty(energy?.vat);
        state.energy.fixedElementPublicId = elementPublicId(element);
        applyChargingExtrasFromElement(state, element);
    }

    state.energy.energyPriceMinExclVat = toNumberOrEmpty(
        tariff.energyPriceMinExclVat ?? tariff.EnergyPriceMinExclVat
    );
    state.energy.energyPriceMaxExclVat = toNumberOrEmpty(
        tariff.energyPriceMaxExclVat ?? tariff.EnergyPriceMaxExclVat
    );
    if ((tariff.isSpotEnergy || tariff.IsSpotEnergy) && !(tariff.isPublic || tariff.IsPublic)) {
        state.energyMode = ENERGY_MODE.SPOT;
    }

    charging.forEach((element) => {
        if (used.has(element)) {
            return;
        }
        const restrictions = getRestrictions(element);
        const isUnrestricted = !hasTimeWindow(restrictions) && !hasPowerWindow(restrictions);
        if (isUnrestricted && hasChargingExtraComponents(element)) {
            used.add(element);
            state.energy.extrasElementPublicId = elementPublicId(element);
            applyChargingExtrasFromElement(state, element);
        }
    });

    if (reservationEls[0]) {
        used.add(reservationEls[0]);
        state.extras.reservation = {
            ...laneFeeFromElement(reservationEls[0]),
            restrictionType: "RESERVATION",
        };
    }

    if (bookingFeeEls[0]) {
        bookingFeeEls.forEach((element) => used.add(element));
        if (!reservationEls[0]) {
            state.extras.reservation = {
                ...laneFeeFromElement(bookingFeeEls[0]),
                restrictionType: "BOOKING",
            };
            state.extras.additionalBookingElements = bookingFeeEls.slice(1);
        } else {
            state.extras.booking = laneFeeFromElement(bookingFeeEls[0]);
            state.extras.additionalBookingElements = bookingFeeEls.slice(1);
        }
    }

    if (noShowEls[0]) {
        noShowEls.forEach((element) => used.add(element));
        const element = noShowEls[0];
        const flat = findComponent(element, "FLAT");
        const booking = getBooking(element);
        state.extras.noShow = {
            enabled: true,
            price: toNumberOrEmpty(componentPrice(flat)),
            vat: toNumberOrEmpty(componentVat(flat)),
            stepSize: 1,
            restrictionType: booking === "BOOKING_EXPIRES" ? "BOOKING_EXPIRES" : "RESERVATION_EXPIRES",
            tariffElementPublicId: elementPublicId(element),
            additionalElements: noShowEls.slice(1),
        };
    }

    if (overtimeEls[0]) {
        used.add(overtimeEls[0]);
        state.extras.overtime = laneFeeFromElement(overtimeEls[0]);
    }

    if (cancellationEls[0]) {
        used.add(cancellationEls[0]);
        state.extras.cancellation = laneFeeFromElement(cancellationEls[0]);
    }

    state.legacyElements = elements.filter((element) => !used.has(element));
    return state;
}

function hasChargingExtraComponents(element) {
    return getComponents(element).some((pc) => {
        const type = componentType(pc);
        return type === "TIME" || type === "PARKING_TIME" || type === "FLAT";
    });
}

function applyChargingExtrasFromElement(state, element) {
    const time = findComponent(element, "TIME");
    const parking = findComponent(element, "PARKING_TIME");
    const flat = findComponent(element, "FLAT");
    if (time) {
        state.extras.chargingTime = {
            enabled: true,
            price: toNumberOrEmpty(componentPrice(time)),
            vat: toNumberOrEmpty(componentVat(time)),
            stepSize: componentStepSize(time) || 1,
        };
    }
    if (parking) {
        state.extras.parkingTime = {
            enabled: true,
            price: toNumberOrEmpty(componentPrice(parking)),
            vat: toNumberOrEmpty(componentVat(parking)),
            stepSize: componentStepSize(parking) || 1,
        };
    }
    if (flat) {
        state.extras.startFee = {
            enabled: true,
            price: toNumberOrEmpty(componentPrice(flat)),
            vat: toNumberOrEmpty(componentVat(flat)),
            stepSize: 1,
        };
    }
}

export function getCreateSeedElements(state) {
    const elements = toElements(state);
    const first = elements[0];
    if (!first) {
        return [{ priceComponents: [priceComponent("ENERGY", 0, 0, 1)] }];
    }
    return [{ priceComponents: first.priceComponents }];
}

export function validateBasics(state) {
    const missing = [];
    if (!state.basics.tariffName) {
        missing.push("tariffName");
    }
    if (!state.basics.currency) {
        missing.push("currency");
    }
    if (!state.basics.countryCode) {
        missing.push("country");
    }
    return missing;
}

export function validatePreauthorizeAmount(state) {
    const value = state.basics.preauthorizeAmount;
    if (value === "" || value == null) {
        return "";
    }
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 3) {
        return "tariff.validation.preauthorizeMin";
    }
    return "";
}

export function validateEnergy(state) {
    if (state.energyMode === ENERGY_MODE.FIXED) {
        if (!Number.isFinite(Number(state.energy.price === "" ? 0 : state.energy.price))) {
            return "tariff.validation.energyPriceNumber";
        }
        return "";
    }
    if (state.energyMode === ENERGY_MODE.SPOT) {
        if (!Number.isFinite(Number(state.energy.price === "" ? 0 : state.energy.price))) {
            return "tariff.validation.spotSurchargeNumber";
        }
        const min = state.energy.energyPriceMinExclVat;
        const max = state.energy.energyPriceMaxExclVat;
        const minFilled = min !== "" && min != null;
        const maxFilled = max !== "" && max != null;
        if (minFilled && !Number.isFinite(Number(min))) {
            return "tariff.validation.spotBoundNumber";
        }
        if (maxFilled && !Number.isFinite(Number(max))) {
            return "tariff.validation.spotBoundNumber";
        }
        if (minFilled && maxFilled && Number(min) > Number(max)) {
            return "tariff.validation.spotMinMax";
        }
        return "";
    }
    if (state.energyMode === ENERGY_MODE.TIME) {
        if (!state.energy.periods.length) {
            return "tariff.validation.addTimePeriod";
        }
        const incomplete = state.energy.periods.some((period) => !period.startTime || !period.endTime);
        if (incomplete) {
            return "tariff.validation.periodStartEnd";
        }
        const crossesMidnight = state.energy.periods.some((period) =>
            periodCrossesMidnight(period.startTime, period.endTime)
        );
        if (crossesMidnight) {
            return "tariff.validation.periodSameDay";
        }
        if (hasFallbackEnergy(state.energy) && !Number.isFinite(Number(state.energy.price))) {
            return "tariff.validation.fallbackNumber";
        }
        return "";
    }
    if (!state.energy.bands.length) {
        return "tariff.validation.addPowerBand";
    }
    const invalidBand = state.energy.bands.some((band) => {
        if (band.minPower === "" || band.minPower == null) {
            return false;
        }
        if (band.maxPower === "" || band.maxPower == null) {
            return false;
        }
        return Number(band.minPower) >= Number(band.maxPower);
    });
    if (invalidBand) {
        return "tariff.validation.bandMinMax";
    }
    if (hasFallbackEnergy(state.energy) && !Number.isFinite(Number(state.energy.price))) {
        return "tariff.validation.fallbackNumber";
    }
    return "";
}

export function buildPriceObject(excl, incl) {
    const exclVal = parseFloat(excl);
    const inclVal = parseFloat(incl);
    const exclOrNull = Number.isNaN(exclVal) ? null : exclVal;
    const inclOrNull = Number.isNaN(inclVal) ? null : inclVal;
    if (exclOrNull === null && inclOrNull === null) {
        return null;
    }
    return {
        excl_vat: exclOrNull,
        incl_vat: inclOrNull,
    };
}

export function buildDecimalOrNull(value) {
    if (value === "" || value == null) {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export function dayOptions(t) {
    return t ? getDayOfWeekOptions(t) : dayOfWeekOptions;
}

export function elementTitle(element, t) {
    const restrictions = getRestrictions(element);
    const types = getComponents(element).map((pc) => componentType(pc));
    const reservation = getReservation(element);
    const booking = getBooking(element);
    const label = (key, fallback, vars) => (t ? t(key, vars) : fallback);

    if (reservation === "RESERVATION") {
        return label("tariff.bookingHold", "Booking hold");
    }
    if (reservation === "RESERVATION_EXPIRES" || booking === "RESERVATION_EXPIRES") {
        return label("tariff.bookingNoShow", "Booking no-show");
    }
    if (booking === "BOOKING_EXPIRES") {
        return label("tariff.bookingNoShow", "Booking no-show");
    }
    if (booking === "BOOKING_OVERTIME") {
        return label("tariff.bookingOvertime", "Booking overtime");
    }
    if (booking === "BOOKING_CANCELLATION_FEES") {
        return label("tariff.bookingCancellation", "Booking cancellation");
    }
    if (booking === "BOOKING") {
        return label("tariff.bookingWindow", "Booking window");
    }
    if (booking) {
        const bookingText = booking.replaceAll("_", " ");
        return label("tariff.bookingOther", `Booking · ${bookingText}`, { booking: bookingText });
    }

    const days = (restrictions.dayOfWeek || []).map((day) => formatDayOfWeekLabel(day, t)).join(", ");
    const time =
        restrictions.startTime || restrictions.endTime
            ? `${toTimeInput(restrictions.startTime) || "—"}–${toTimeInput(restrictions.endTime) || "—"}`
            : "";
    const power =
        restrictions.minPower != null || restrictions.maxPower != null
            ? `${restrictions.minPower ?? 0}–${restrictions.maxPower ?? "+"} kW`
            : "";

    if (types.includes("ENERGY") && time) {
        const detail = [days, time].filter(Boolean).join(" ");
        return label("tariff.energyTime", `Energy · ${detail}`, { detail });
    }
    if (types.includes("ENERGY") && power) {
        return label("tariff.energyPower", `Energy · ${power}`, { power });
    }
    if (types.includes("ENERGY")) {
        return label("tariff.energy247", "Energy 24/7");
    }
    if (types.includes("TIME") && types.includes("PARKING_TIME")) {
        return label("tariff.chargingAndParking", "Charging and parking time");
    }
    if (types.includes("TIME")) {
        return label("tariff.chargingTime", "Charging time");
    }
    if (types.includes("PARKING_TIME")) {
        return label("tariff.parkingTime", "Parking time");
    }
    if (types.includes("FLAT")) {
        return label("tariff.startFee", "Start fee");
    }
    return restrictionSummary(element, t);
}

export function formatElementsInUseError(message, originalElements = [], nextElements = [], t) {
    if (!isElementsInUseError(message)) {
        return message || (t ? t("tariff.failedSave") : "Failed to save tariff");
    }

    const keptIds = new Set(
        (nextElements || []).map((element) => elementPublicId(element)).filter(Boolean)
    );
    const names = [];
    const seen = new Set();
    (originalElements || []).forEach((element) => {
        const id = elementPublicId(element);
        if (!id || keptIds.has(id)) {
            return;
        }
        const name = elementTitle(element, t);
        if (!name || seen.has(name)) {
            return;
        }
        seen.add(name);
        names.push(name);
    });

    if (!names.length) {
        return t ? t("tariff.elementsInUse") : "These prices have already been used on a charging session and cannot be removed. Change the prices instead of removing them.";
    }
    const listed = joinUserFacingNames(names, t);
    if (names.length === 1) {
        return t
            ? t("tariff.elementInUseOne", { name: listed })
            : `${listed} has already been used on a charging session and cannot be removed. Change that price instead of removing it.`;
    }
    return t
        ? t("tariff.elementInUseMany", { names: listed })
        : `${listed} have already been used on a charging session and cannot be removed. Change those prices instead of removing them.`;
}

function isElementsInUseError(message) {
    const text = String(message || "");
    return (
        text.includes("priced a charging period") ||
        text.includes("already been used on a charging session") ||
        text.includes("TARIFF_ELEMENT_IN_USE")
    );
}

function joinUserFacingNames(names, t) {
    if (names.length === 1) {
        return names[0];
    }
    const and = t ? t("common.and") : "and";
    if (names.length === 2) {
        return `${names[0]} ${and} ${names[1]}`;
    }
    return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`;
}

export function restrictionSummary(element, t) {
    const restrictions = element?.restrictions || {};
    const parts = [];
    const days = (restrictions.dayOfWeek || []).map((day) => formatDayOfWeekLabel(day, t));
    if (days.length) {
        parts.push(days.join(", "));
    }
    if (restrictions.startTime || restrictions.endTime) {
        parts.push(`${toTimeInput(restrictions.startTime) || "—"}–${toTimeInput(restrictions.endTime) || "—"}`);
    }
    if (restrictions.minPower != null || restrictions.maxPower != null) {
        parts.push(`${restrictions.minPower ?? 0}–${restrictions.maxPower ?? "+"} kW`);
    }
    if (restrictions.reservation) {
        const reservationLabel = t
            ? t(`tariff.reservationType.${restrictions.reservation}`, { defaultValue: restrictions.reservation })
            : restrictions.reservation;
        parts.push(
            t
                ? t("tariff.restriction.reservation", { value: reservationLabel })
                : `Reservation: ${restrictions.reservation}`
        );
    }
    if (restrictions.booking) {
        const bookingLabel = t
            ? t(`tariff.booking.${restrictions.booking}`, { defaultValue: restrictions.booking })
            : restrictions.booking;
        parts.push(
            t
                ? t("tariff.restriction.booking", { value: bookingLabel })
                : `Booking: ${restrictions.booking}`
        );
    }
    if (restrictions.minDuration != null || restrictions.maxDuration != null) {
        const min = restrictions.minDuration ?? "—";
        const max = restrictions.maxDuration ?? "—";
        parts.push(
            t
                ? t("tariff.restriction.duration", { min, max })
                : `Duration ${min}–${max} s`
        );
    }
    return parts.join(" · ") || (t ? t("tariff.restriction.none") : "No restrictions");
}
